import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type SellerBatchRow, type SellerListingRow } from '@chicare/shared';

import {
  useAddBatch,
  useAdjustBatch,
  useCreateListing,
  useProductSearch,
  useSellerListing,
  useSellerListings,
  useUpdateListing,
} from '@/lib/onboardingQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * What this pharmacy sells.
 *
 * ── §9.1: PRICE AND STOCK, NEVER A DRUG FACT ────────────────────────────────
 *
 * A pharmacy picks a product from ChiCare's catalogue and sets what it costs
 * here. The name, strength, NAFDAC number and whether it is prescription-only
 * are shown read-only, because they belong to the product and not to any one
 * seller. Six pharmacies each typing their own dosage text for one medicine is
 * six competing claims about a drug, and one of them eventually hurts somebody.
 *
 * ── A LISTING WITHOUT A BATCH IS INVISIBLE ──────────────────────────────────
 *
 * The offer ladder only shows listings with sellable stock behind them, so the
 * screen says so plainly rather than letting a pharmacist add ten products and
 * wonder why nobody is buying.
 */
export function ListingsScreen() {
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, error, refetch } = useSellerListings();

  /**
   * Their own shelf, filtered.
   *
   * The search at the top of "Add a product" looks through ChiCare's
   * CATALOGUE — four thousand medicines, to find one to start stocking. This
   * one looks through the two hundred this pharmacy actually sells, which is a
   * different question asked far more often: "what am I charging for
   * amoxicillin again".
   *
   * Client-side because the list is already in hand and a round trip to filter
   * two hundred rows is a round trip for nothing.
   */
  const needle = search.trim().toLowerCase();

  const shown = (data?.listings ?? []).filter((listing) => {
    if (needle === '') {
      return true;
    }

    return [
      listing.product.name,
      listing.product.category,
      listing.price_display,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });

  if (isError) {
    return <AdminError title="We couldn't load your products" error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-5 xl:px-8">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">Products</h1>
          <p className="mt-1 text-[13px] text-muted">
            Set your price and your stock. Everything else comes from the ChiCare catalogue.
          </p>
        </div>

        {data?.meta.can_sell ? (
          <Button icon="plus" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : 'Add a product'}
          </Button>
        ) : null}
      </header>

      {data && !data.meta.can_sell ? (
        <Card className="mb-4 border-caution bg-caution-tint">
          <p className="m-0 text-[13.5px] font-bold text-ink">You cannot list yet</p>
          <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
            {data.meta.pharmacy_status === 'suspended'
              ? 'Your listings are paused. Check your Money screen — an outstanding balance or a lapsed licence is the usual reason.'
              : 'Finish your registration and we will review it. Nothing goes on sale until your PCN licence is checked.'}
          </p>
          <Link
            to="/seller/onboarding"
            className="mt-3 inline-flex min-h-11 items-center rounded-btn bg-blue px-4 text-[13.5px] font-bold text-white no-underline"
          >
            Finish registration
          </Link>
        </Card>
      ) : null}

      {adding ? <AddListing onDone={() => setAdding(false)} /> : null}

      {data && data.meta.total > 0 ? (
        <div className="mb-4 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-muted">
          <span>
            <b className="font-mono text-ink">{data.meta.total}</b> listed
          </span>
          {data.meta.out_of_stock > 0 ? (
            <span className="text-danger">
              <b className="font-mono">{data.meta.out_of_stock}</b> with no stock — invisible to buyers
            </span>
          ) : null}
          {data.meta.below_reorder > 0 ? (
            <span className="text-caution">
              <b className="font-mono">{data.meta.below_reorder}</b> running low
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Only once there is enough to be worth hunting through. A search box
          over four products is furniture. */}
      {!isLoading && data && data.listings.length > 6 ? (
        <label className="relative mb-3 block">
          <span className="sr-only">Search your products</span>
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
          />
          <input
            type="search"
            value={search}
            data-testid="listings-search"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search your products"
            className="min-h-11 w-full rounded-btn border border-line bg-white pl-9 pr-3 text-[14px] text-ink outline-none focus:border-blue"
          />
        </label>
      ) : null}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" radius="card" />
          <Skeleton className="h-24 w-full" radius="card" />
        </div>
      ) : data && data.listings.length === 0 ? (
        <EmptyState
          title="Nothing listed yet"
          body="Search the ChiCare catalogue, set your price, and add a batch. That is all it takes to go on sale."
          action={
            data.meta.can_sell ? (
              <Button icon="plus" onClick={() => setAdding(true)}>
                Add your first product
              </Button>
            ) : (
              <Button variant="ghost" icon="refresh" onClick={() => refetch()}>
                Check again
              </Button>
            )
          }
        />
      ) : shown.length === 0 ? (
        <EmptyState
          title="Nothing matches that"
          body="Try part of the medicine name, or the category it is filed under."
          action={
            <Button variant="grey" onClick={() => setSearch('')}>
              Clear the search
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {shown.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              open={open === listing.id}
              onToggle={() => setOpen(open === listing.id ? null : listing.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Find a product in the catalogue, then say what it costs here. */
function AddListing({ onDone }: { onDone: () => void }) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<{ id: number; name: string } | null>(null);
  const [naira, setNaira] = useState('');

  const search = useProductSearch(query);
  const create = useCreateListing();

  const priceKobo = Math.round(Number(naira || 0) * 100);

  return (
    <Card className="mb-4">
      <h2 className="m-0 mb-3 font-display text-[15px] font-bold tracking-[-0.01em]">
        Add a product
      </h2>

      {!picked ? (
        <>
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
              Search the ChiCare catalogue
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Paracetamol, Ventolin, glucometer…"
              className="min-h-12 w-full rounded-btn border border-line bg-paper px-3 text-[14px] text-ink outline-none focus:border-blue"
            />
          </label>

          {query.trim().length >= 2 ? (
            <div className="mt-3 space-y-2">
              {search.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : search.data?.products.length === 0 ? (
                <p className="m-0 text-[13px] text-muted">
                  Nothing matched. ChiCare curates the catalogue centrally — if a medicine is
                  missing, tell us and we will add it.
                </p>
              ) : (
                search.data?.products.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => setPicked({ id: product.id, name: product.display_name })}
                    className="flex w-full items-center gap-3 rounded-card border border-line bg-white p-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-[13.5px] font-semibold text-ink">
                        {product.display_name}
                      </p>
                      <p className="m-0 mt-0.5 text-[12px] text-muted">
                        {[product.form, product.pack_size, product.manufacturer]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    {product.is_pom ? <Badge tone="caution">POM</Badge> : null}
                    {product.is_cold_chain ? <Badge tone="brand">Cold chain</Badge> : null}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3 rounded-card border border-line bg-paper p-3">
            <p className="m-0 text-[13.5px] font-semibold text-ink">{picked.name}</p>
            <Button size="sm" variant="grey" onClick={() => setPicked(null)}>
              Change
            </Button>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
              Your price (₦)
            </span>
            <input
              inputMode="decimal"
              value={naira}
              onChange={(event) => setNaira(event.target.value.replace(/[^\d.]/g, ''))}
              placeholder="900"
              className="min-h-12 w-full rounded-btn border border-line bg-paper px-3 font-mono text-[15px] tabular-nums text-ink outline-none focus:border-blue"
            />
          </label>

          {create.error ? (
            <p role="alert" className="mt-2 text-[12.5px] font-medium text-danger">
              {create.error instanceof ApiError ? create.error.message : 'That did not save.'}
            </p>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              disabled={priceKobo < 100 || create.isPending}
              onClick={() =>
                create.mutate(
                  { productId: picked.id, priceKobo },
                  { onSuccess: onDone },
                )
              }
            >
              {create.isPending ? 'Adding…' : 'Add to my shop'}
            </Button>
            <Button variant="ghost" onClick={onDone}>
              Cancel
            </Button>
          </div>

          <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-muted">
            It goes on sale once you add a batch with its expiry date.
          </p>
        </>
      )}
    </Card>
  );
}

function ListingCard({
  listing,
  open,
  onToggle,
}: {
  listing: SellerListingRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className={cn('overflow-hidden p-0', open && 'ring-1 ring-blue')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 p-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-bold text-ink">{listing.product.name}</span>
            {listing.product.is_pom ? <Badge tone="caution">POM</Badge> : null}
            {listing.product.is_cold_chain ? <Badge tone="brand">Cold chain</Badge> : null}
          </div>

          <p className="m-0 mt-1 text-[12.5px]">
            {listing.on_sale ? (
              <span className="text-verified">On sale</span>
            ) : listing.is_active ? (
              <span className="font-medium text-danger">No stock — buyers cannot see this</span>
            ) : (
              <span className="text-muted">Off sale</span>
            )}
            {listing.needs_restock && listing.on_sale ? (
              <span className="text-caution"> · running low</span>
            ) : null}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p className="m-0 font-mono text-[15px] font-bold tabular-nums text-ink">
            {listing.price_display}
          </p>
          {/* §1.5 — under the markup model the shop charges more than this,
              and the difference is ChiCare's. A pharmacist who sees only one
              number here and a different one on the product page concludes
              the console is lying to them, so the console says it first. */}
          {listing.pricing_model === 'markup' ? (
            <p className="m-0 font-mono text-[11px] text-faint">
              you receive · buyer pays {listing.buyer_price_display}
            </p>
          ) : null}
          <p className="m-0 font-mono text-[11.5px] text-muted">
            {listing.available_quantity} in stock
          </p>
        </div>
      </button>

      {open ? <ListingDetail id={listing.id} listing={listing} /> : null}
    </Card>
  );
}

function ListingDetail({ id, listing }: { id: number; listing: SellerListingRow }) {
  const { data, isLoading, isError, error, refetch } = useSellerListing(id);
  const update = useUpdateListing();

  const [naira, setNaira] = useState(String(Math.round(listing.price_kobo / 100)));

  if (isError) {
    return (
      <div className="border-t border-line p-4">
        <AdminError title="We couldn't load this product" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 border-t border-line p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="border-t border-line bg-paper p-4">
      <section className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
            {listing.pricing_model === 'markup' ? 'What you receive (₦)' : 'Price (₦)'}
          </span>
          <input
            inputMode="decimal"
            value={naira}
            onChange={(event) => setNaira(event.target.value.replace(/[^\d.]/g, ''))}
            className="min-h-11 w-[140px] rounded-btn border border-line bg-white px-3 font-mono text-[14px] tabular-nums text-ink outline-none focus:border-blue"
          />
          {/* Computed from the SAVED price, and shown only while the box still
              holds it. A preview that guessed at the rate would be a second
              implementation of the pricing rules living in the browser, and the
              two would disagree the first time a rate changed. Saying nothing
              is better than saying a number that turns out to be wrong. */}
          {listing.pricing_model === 'markup' ? (
            <span className="mt-1 block text-[11.5px] leading-snug text-muted">
              {Math.round(Number(naira || 0) * 100) === listing.price_kobo ? (
                <>
                  Buyers pay <b className="text-ink">{listing.buyer_price_display}</b>. The
                  difference is ChiCare's.
                </>
              ) : (
                'Save to see what buyers will pay.'
              )}
            </span>
          ) : null}
        </label>

        <Button
          size="sm"
          disabled={update.isPending}
          onClick={() => update.mutate({ id, priceKobo: Math.round(Number(naira || 0) * 100) })}
        >
          {update.isPending ? 'Saving…' : 'Save price'}
        </Button>

        <Button
          size="sm"
          variant="grey"
          onClick={() => update.mutate({ id, isActive: !listing.is_active })}
        >
          {listing.is_active ? 'Take off sale' : 'Put back on sale'}
        </Button>
      </section>

      <Stock listingId={id} batches={data.listing.batches} minimumExpiry={data.minimumExpiryDate} />
    </div>
  );
}

/**
 * Batches. The physical cartons on the shelf.
 *
 * §9.3: the minimum expiry date is stated up front, because a pharmacist
 * needs to know a carton is unsellable while they can still send it back —
 * not after they have shelved it.
 */
function Stock({
  listingId,
  batches,
  minimumExpiry,
}: {
  listingId: number;
  batches: SellerBatchRow[];
  minimumExpiry: string;
}) {
  const add = useAddBatch();
  const [form, setForm] = useState({ batch_no: '', expiry_date: '', quantity: '' });

  const valid =
    form.batch_no.trim().length > 0 &&
    form.expiry_date !== '' &&
    Number(form.quantity) > 0;

  return (
    <section>
      <h3 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
        Stock
      </h3>

      {batches.length > 0 ? (
        <ul className="m-0 mb-3 list-none space-y-2 p-0">
          {batches.map((batch) => (
            <BatchRow key={batch.id} batch={batch} />
          ))}
        </ul>
      ) : (
        <p className="m-0 mb-3 text-[13px] text-muted">
          No stock yet. Buyers cannot see this product until you add a batch.
        </p>
      )}

      <div className="rounded-card border border-line bg-white p-3">
        <p className="m-0 mb-2.5 text-[13px] font-bold text-ink">Add a batch</p>

        <div className="grid gap-2.5 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-[11.5px] font-medium text-muted">Batch number</span>
            <input
              value={form.batch_no}
              onChange={(event) => setForm({ ...form, batch_no: event.target.value })}
              placeholder="B24F0017"
              className="min-h-11 w-full rounded-btn border border-line bg-paper px-3 font-mono text-[13.5px] text-ink outline-none focus:border-blue"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11.5px] font-medium text-muted">Expiry</span>
            <input
              type="date"
              min={minimumExpiry}
              value={form.expiry_date}
              onChange={(event) => setForm({ ...form, expiry_date: event.target.value })}
              className="min-h-11 w-full rounded-btn border border-line bg-paper px-3 text-[13.5px] text-ink outline-none focus:border-blue"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11.5px] font-medium text-muted">Units</span>
            <input
              inputMode="numeric"
              value={form.quantity}
              onChange={(event) =>
                setForm({ ...form, quantity: event.target.value.replace(/\D/g, '') })
              }
              placeholder="40"
              className="min-h-11 w-full rounded-btn border border-line bg-paper px-3 font-mono text-[13.5px] tabular-nums text-ink outline-none focus:border-blue"
            />
          </label>
        </div>

        {add.error ? (
          <p role="alert" className="mt-2 text-[12.5px] font-medium leading-relaxed text-danger">
            {add.error instanceof ApiError ? add.error.message : 'That batch was not accepted.'}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            disabled={!valid || add.isPending}
            onClick={() =>
              add.mutate(
                {
                  listingId,
                  batchNo: form.batch_no.trim(),
                  expiryDate: form.expiry_date,
                  quantity: Number(form.quantity),
                },
                { onSuccess: () => setForm({ batch_no: '', expiry_date: '', quantity: '' }) },
              )
            }
          >
            {add.isPending ? 'Adding…' : 'Add batch'}
          </Button>

          {minimumExpiry ? (
            <p className="m-0 text-[11.5px] leading-relaxed text-muted">
              Must expire on or after <b className="font-mono text-ink">{minimumExpiry}</b>. Anything
              shorter cannot be sold here — send it back to your supplier.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function BatchRow({ batch }: { batch: SellerBatchRow }) {
  const adjust = useAdjustBatch();
  const [editing, setEditing] = useState(false);
  const [quantity, setQuantity] = useState(String(batch.quantity));
  const [reason, setReason] = useState('');

  const tone =
    batch.expiry_state === 'breach'
      ? 'border-danger bg-danger-tint'
      : batch.expiry_state === 'warning'
        ? 'border-caution bg-caution-tint'
        : 'border-line bg-white';

  return (
    <li className={cn('rounded-card border p-3', tone)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 font-mono text-[13px] font-semibold text-ink">{batch.batch_no}</p>
          <p className="m-0 mt-0.5 text-[12px] text-muted">
            Expires {batch.expiry_date} · {batch.available} available
            {batch.reserved_quantity > 0 ? ` · ${batch.reserved_quantity} committed to orders` : null}
          </p>
        </div>

        <Button size="sm" variant="grey" onClick={() => setEditing(!editing)}>
          {editing ? 'Cancel' : 'Adjust'}
        </Button>
      </div>

      {editing ? (
        <div className="mt-2.5 grid gap-2.5 sm:grid-cols-[110px_1fr_auto] sm:items-end">
          <label className="block">
            <span className="mb-1 block text-[11.5px] font-medium text-muted">New total</span>
            <input
              inputMode="numeric"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value.replace(/\D/g, ''))}
              className="min-h-11 w-full rounded-btn border border-line bg-white px-3 font-mono text-[13.5px] tabular-nums text-ink outline-none focus:border-blue"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11.5px] font-medium text-muted">What changed?</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Six blisters crushed in the crate"
              className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13.5px] text-ink outline-none focus:border-blue"
            />
          </label>
          <Button
            size="sm"
            disabled={reason.trim().length < 3 || adjust.isPending}
            onClick={() =>
              adjust.mutate(
                { batchId: batch.id, quantity: Number(quantity), reason },
                { onSuccess: () => setEditing(false) },
              )
            }
          >
            Save
          </Button>

          {adjust.error ? (
            <p role="alert" className="text-[12.5px] font-medium text-danger sm:col-span-3">
              {adjust.error instanceof ApiError ? adjust.error.message : 'That did not save.'}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
